import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Redirect, useHistory, useParams } from 'react-router';
import { z } from 'zod';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import { DeleteConfirmationDialog } from '@rossum/components/delete-confirmation-dialog/DeleteConfirmationDialog';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { EmptyStateRadar } from '@rossum/ui';
import { Stack } from '@rossum/ui/material';
import { boldText } from '../../../lib/formaterValues';
import { HelmetComponent } from '../../../routes/HelmetComponent';
import { notify } from '../../toaster';
import {
  Credential,
  CREDENTIAL_FORM_ID,
  CREDENTIAL_ID_KEY,
  credentialFormSchema,
  CredentialFormValues,
  CredentialParams,
  credentialsPath,
  toCredentialFormValues,
} from '../core';
import {
  useDeleteCredential,
  useGetCredential,
  useSubmitCredential,
} from '../core/hooks';
import { CredentialExtensionsList } from './CredentialExtensionsList';
import { CredentialForm } from './CredentialForm';
import { CredentialFormHeader } from './CredentialFormHeader';
import { CredentialFormSkeleton } from './CredentialFormSkeleton';

type CredentialFormPageProps = {
  /** Undefined while creating a new credential. */
  credential?: Credential;
};

const CredentialFormPage = ({ credential }: CredentialFormPageProps) => {
  const intl = useIntl();
  const history = useHistory();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<CredentialFormValues>({
    defaultValues: toCredentialFormValues(credential),
    resolver: zodResolver(credentialFormSchema(intl)),
  });

  const { submit } = useSubmitCredential({
    credentialId: credential?.id,
    onSuccess: savedCredential => {
      if (credential) {
        // Editing keeps the user on the form, so the fields are reset to the
        // stored credential rather than to what was typed.
        reset(toCredentialFormValues(savedCredential));
      } else {
        history.push(credentialsPath());
      }
    },
  });

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { mutate: deleteCredential, isLoading: isDeleting } =
    useDeleteCredential();

  const handleDelete = (credentialId: number) =>
    deleteCredential(credentialId, {
      onSuccess: () => {
        notify.default({
          title: intl.formatMessage({
            id: 'features.credentials.form.notification.deleted',
          }),
        });
        history.push(credentialsPath());
      },
      onError: () => {
        setIsDeleteDialogOpen(false);
        notify.error({
          title: intl.formatMessage({
            id: 'features.credentials.form.notification.deleteFailed',
          }),
        });
      },
    });

  return (
    <>
      <HelmetComponent translationKey="features.credentials.header.title" />
      <PageLayout
        renderHeader={params => (
          <CredentialFormHeader
            {...params}
            credential={credential}
            isEditing={credential !== undefined}
            isMutating={isSubmitting || isDeleting}
            onDelete={
              credential ? () => setIsDeleteDialogOpen(true) : undefined
            }
          />
        )}
      >
        <Stack
          spacing={2}
          useFlexGap
          id={CREDENTIAL_FORM_ID}
          component="form"
          onSubmit={handleSubmit(submit)}
          sx={{ px: 4, py: 4 }}
        >
          <CredentialForm
            control={control}
            isEditing={credential !== undefined}
            disabled={isSubmitting}
          />
          {credential ? (
            <ContentGroup
              title={intl.formatMessage({
                id: 'features.credentials.form.extensions.title',
              })}
              description={intl.formatMessage({
                id: 'features.credentials.form.extensions.description',
              })}
            >
              <CredentialExtensionsList hooks={credential.hooks} />
            </ContentGroup>
          ) : null}
        </Stack>
      </PageLayout>
      {credential ? (
        <DeleteConfirmationDialog
          open={isDeleteDialogOpen}
          title={intl.formatMessage({
            id: 'features.credentials.form.deleteDialog.title',
          })}
          description={
            credential.hooks.length > 0
              ? intl.formatMessage(
                  { id: 'features.credentials.form.deleteDialog.description' },
                  {
                    boldText,
                    name: credential.name,
                    count: credential.hooks.length,
                  }
                )
              : intl.formatMessage(
                  {
                    id: 'features.credentials.form.deleteDialog.description.unused',
                  },
                  { boldText, name: credential.name }
                )
          }
          isLoading={isDeleting}
          onCancel={() => setIsDeleteDialogOpen(false)}
          onConfirm={() => handleDelete(credential.id)}
        />
      ) : null}
    </>
  );
};

export const CredentialCreatePage = () => <CredentialFormPage />;

export const CredentialDetailPage = () => {
  const intl = useIntl();
  const params = useParams<CredentialParams>();

  const parsedId = z.coerce.number().int().safeParse(params[CREDENTIAL_ID_KEY]);

  const {
    data: credential,
    isLoading,
    isError,
  } = useGetCredential(parsedId.success ? parsedId.data : 0, {
    enabled: parsedId.success,
  });

  if (!parsedId.success) {
    return <Redirect to={credentialsPath()} />;
  }

  if (isLoading || isError || !credential) {
    return (
      <>
        <HelmetComponent translationKey="features.credentials.header.title" />
        <PageLayout
          renderHeader={headerParams => (
            <CredentialFormHeader
              {...headerParams}
              isEditing
              isLoading={isLoading}
            />
          )}
        >
          <Stack spacing={4} useFlexGap sx={{ px: 4, py: 4 }}>
            {isLoading ? (
              <CredentialFormSkeleton />
            ) : (
              <EmptyStateRadar
                title={intl.formatMessage({
                  id: 'features.credentials.error.title',
                })}
                description={intl.formatMessage({
                  id: 'features.credentials.error.description',
                })}
              />
            )}
          </Stack>
        </PageLayout>
      </>
    );
  }

  return <CredentialFormPage credential={credential} />;
};
